/**
 * Normalized question model shared by the questionnaire-template editor and
 * the create-study wizard. Each call site converts its own persistence model
 * to/from these shapes (see adapters next to each usage).
 */

export type QuestionKind = 'text' | 'single' | 'multi' | 'scale' | 'nps' | 'rank'

export interface EditableOption {
  id: string
  text: string
  /** Optional numeric value (used by questionnaire templates). */
  value?: number
}

export interface EditableQuestion {
  id: string
  kind: QuestionKind
  text: string
  options: EditableOption[]
  required: boolean
  /** Optional section grouping (used by questionnaire templates). */
  section?: string
}

/** Kinds that present a list of answer options the author edits. */
export const KINDS_WITH_OPTIONS: QuestionKind[] = ['single', 'multi', 'rank']

export function kindHasOptions(kind: QuestionKind): boolean {
  return KINDS_WITH_OPTIONS.includes(kind)
}

/** How the editor should be tailored for a given context. */
export interface QuestionEditorConfig {
  /** Which question kinds the author may pick. */
  allowedKinds: QuestionKind[]
  /** Show the per-question "required" switch. */
  showRequiredToggle?: boolean
  /** Show the optional "section" field. */
  showSection?: boolean
  /** Show an editable numeric value next to each option. */
  showOptionValues?: boolean
}

let counter = 0
/** Stable client-side id for new questions/options (React keys + tracking). */
export function newId(prefix = 'q'): string {
  counter += 1
  return `${prefix}-${counter}-${(counter * 2654435761) % 100000}`
}

/** A fresh option, optionally pre-numbered for templates that use values. */
export function makeOption(index: number, withValue: boolean): EditableOption {
  return { id: newId('opt'), text: '', value: withValue ? index + 1 : undefined }
}

/** A fresh question seeded with sensible defaults for its kind. */
export function makeQuestion(kind: QuestionKind, withValues: boolean): EditableQuestion {
  return {
    id: newId('q'),
    kind,
    text: '',
    required: true,
    options: kindHasOptions(kind) ? [makeOption(0, withValues), makeOption(1, withValues)] : [],
  }
}
